import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X } from 'lucide-react';
import { api } from '../services/api';

export interface AuditLogEntry {
  id: string;
  userId: number;
  userName: string;
  action: string;
  entityType: 'audit' | 'user' | 'patient' | 'system';
  entityId: string;
  details: Record<string, any>;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
}

export interface AuditLogFilter {
  userId?: number;
  action?: string;
  entityType?: string;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
}

interface AuditLogContextType {
  logs: AuditLogEntry[];
  loading: boolean;
  error: string | null;
  logAction: (action: string, entityType: string, entityId: string, details?: Record<string, any>) => Promise<void>;
  fetchLogs: (filter?: AuditLogFilter) => Promise<void>;
  exportLogs: (filter?: AuditLogFilter) => Promise<void>;
  clearError: () => void;
}

const AuditLogContext = createContext<AuditLogContextType | undefined>(undefined);

export const useAuditLog = () => {
  const context = useContext(AuditLogContext);
  if (!context) {
    throw new Error('useAuditLog must be used within an AuditLogProvider');
  }
  return context;
};

interface AuditLogProviderProps {
  children: ReactNode;
}

export const AuditLogProvider: React.FC<AuditLogProviderProps> = ({ children }) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const logAction = useCallback(async (
    action: string,
    entityType: string,
    entityId: string,
    details: Record<string, any> = {}
  ) => {
    try {
      const logData = {
        action,
        entity_type: entityType,
        entity_id: entityId,
        details: JSON.stringify(details),
        timestamp: new Date().toISOString(),
      };

      await api.post('/audit-logs.php', logData);
    } catch (err) {
      console.error('Failed to log action:', err);
      // Don't throw error to avoid breaking the main action
    }
  }, []);

  const fetchLogs = useCallback(async (filter?: AuditLogFilter) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filter?.userId) params.append('user_id', filter.userId.toString());
      if (filter?.action) params.append('action', filter.action);
      if (filter?.entityType) params.append('entity_type', filter.entityType);
      if (filter?.dateFrom) params.append('date_from', filter.dateFrom.toISOString());
      if (filter?.dateTo) params.append('date_to', filter.dateTo.toISOString());
      if (filter?.search) params.append('search', filter.search);

      const response = await api.get(`/audit-logs.php?${params.toString()}`);
      
      if (response.data.success) {
        setLogs(response.data.logs.map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp),
          details: typeof log.details === 'string' ? JSON.parse(log.details) : log.details,
        })));
      } else {
        setError(response.data.message || 'Failed to fetch audit logs');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, []);

  const exportLogs = useCallback(async (filter?: AuditLogFilter) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filter?.userId) params.append('user_id', filter.userId.toString());
      if (filter?.action) params.append('action', filter.action);
      if (filter?.entityType) params.append('entity_type', filter.entityType);
      if (filter?.dateFrom) params.append('date_from', filter.dateFrom.toISOString());
      if (filter?.dateTo) params.append('date_to', filter.dateTo.toISOString());
      if (filter?.search) params.append('search', filter.search);
      params.append('export', 'true');

      const response = await api.get(`/audit-logs.php?${params.toString()}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export audit logs');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <AuditLogContext.Provider
      value={{
        logs,
        loading,
        error,
        logAction,
        fetchLogs,
        exportLogs,
        clearError,
      }}
    >
      {children}
    </AuditLogContext.Provider>
  );
};

// Audit Log Viewer Component
export const AuditLogViewer: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const { logs, loading, error, fetchLogs, exportLogs, clearError } = useAuditLog();
  const [filter, setFilter] = useState<AuditLogFilter>({});

  React.useEffect(() => {
    if (isOpen) {
      fetchLogs(filter);
    }
  }, [isOpen, filter, fetchLogs]);

  const handleExport = () => {
    exportLogs(filter);
  };

  const getActionColor = (action: string) => {
    if (action.includes('create') || action.includes('add')) return 'text-success-600 bg-success-100';
    if (action.includes('update') || action.includes('edit')) return 'text-warning-600 bg-warning-100';
    if (action.includes('delete') || action.includes('remove')) return 'text-danger-600 bg-danger-100';
    if (action.includes('login') || action.includes('logout')) return 'text-info-600 bg-info-100';
    return 'text-neutral-600 bg-neutral-100';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Audit Logs</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <select
                  value={filter.action || ''}
                  onChange={(e) => setFilter({ ...filter, action: e.target.value || undefined })}
                  className="medical-input"
                >
                  <option value="">All Actions</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                  <option value="login">Login</option>
                  <option value="logout">Logout</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
                <select
                  value={filter.entityType || ''}
                  onChange={(e) => setFilter({ ...filter, entityType: e.target.value || undefined })}
                  className="medical-input"
                >
                  <option value="">All Types</option>
                  <option value="audit">Audit</option>
                  <option value="user">User</option>
                  <option value="patient">Patient</option>
                  <option value="system">System</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleExport}
                  className="medical-button-primary w-full"
                  disabled={loading}
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Logs List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full text-danger-600">
                <p>{error}</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <p className="text-lg font-medium">No audit logs found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                            {log.action}
                          </span>
                          <span className="text-sm text-gray-500">{log.entityType}</span>
                          <span className="text-sm text-gray-500">ID: {log.entityId}</span>
                        </div>
                        <p className="text-sm text-gray-900 mb-1">
                          <strong>{log.userName}</strong> performed <strong>{log.action}</strong> on {log.entityType}
                        </p>
                        {Object.keys(log.details).length > 0 && (
                          <div className="text-sm text-gray-600">
                            <details className="cursor-pointer">
                              <summary className="font-medium">Details</summary>
                              <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>{log.timestamp.toLocaleDateString()}</p>
                        <p>{log.timestamp.toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

